import React, { Component } from 'react'
import { Form, HelpBlock } from 'react-bootstrap'
import '../App.css'
import { embedComponent } from '../embed'
import Field from '../components/Field'
import CurrencyField from '../components/CurrencyField'
import formatCurrency from '../utils/formatters/formatCurrency'
import percentChoices from '../utils/forms/percentChoices'
import numberWithLabelChoices from '../utils/forms/numberWithLabelChoices'
import requiredPaymentToGoal from '../utils/calculations/requiredPaymentToGoal'
import CalculatorResults from '../components/CalculatorResults/index'
import CalculatorResult from '../components/CalculatorResult/index'
import Calculator from '../components/Calculator/index'
import PercentageField from '../components/PercentageField/index'
import CalculatorButtons from '../components/CalculatorButtons/index'
import { minMaxValidator } from '../utils/validators/index'
import formatPercentage from '../utils/formatters/formatPercentage'
import Annotations from '../components/Annotations/index'
import { DEFAULT_MAX_DOLLAR_VALUE } from '../utils/validators'

const popoverMessages = {
  priceOfHouse: {
    title: 'Price of house',
    body: [
      'This is the price of the house.',
    ],
  },
  percentDownPayment: {
    title: 'Percent down payment',
    body: [
      'In most cases, buyers must make a down payment.',
      'If the down payment is less than 20%, the buyer may have to buy private mortgage insurance. <a href="/#/articles/273" target="_blank">Click here to learn more about PMI</a>.',
    ],
  },
  downPayment: {
    title: 'Down payment',
    body: [
      'This is calculated based on the price and percent down payment entered.',
      'You can overwrite this amount and enter a specific number.',
      'This number is used in subsequent calculations.',
    ],
  },
  currentSavings: {
    title: 'Current savings',
    body: [
      'This is the amount you have now in your house buying fund.',
      'It might include money you can borrow from family – it is the total amount you have available to put toward a house.',
    ],
  },
  timeToGoal: {
    title: 'Time to goal',
    body: [
      'This is the time you are giving yourself to reach your savings goal.',
      'The shorter you make it, the higher the monthly contributions required will be.',
    ],
  },
  interestOnSavings: {
    title: 'Interest on savings',
    body: [
      'This is the annual interest rate you get on your savings.',
    ],
  },
  monthlySavingsAmount: {
    title: 'You need to save this amount/month',
    body: [
      'This is the amount you need to set aside each month for the number of months you indicated to save the amount you need.',
    ],
  },
}

const percentDownPaymentOptions = percentChoices([5, 10, 15, 20, 25, 30])
const timeToGoalOptions = numberWithLabelChoices([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], ' years', ' year')

class HomeBuyingSavingsCalculator extends Component {
  constructor (props) {
    super(props)

    this.state = HomeBuyingSavingsCalculator.getInitialState()

    this.clearState = this.clearState.bind(this);
    this.onValidation = this.onValidation.bind(this);
    this.getCalculatorValues = this.getCalculatorValues.bind(this);
    this.setCalculatorValues = this.setCalculatorValues.bind(this);
    this.removeEventListeners = this.removeEventListeners.bind(this);
  }

  static getInitialState() {
    return {
      priceOfHouse: null,
      percentDownPayment: 20,
      downPayment: 0,
      currentSavings: null,
      timeToGoal: 5,
      interestOnSavings: null,
      fieldErrors: {},
    }
  }

  componentDidMount() {
    document.addEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.addEventListener('getCalculatorValuesHomeBuyingSavingsCalculator', this.getCalculatorValues, true);
    document.addEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  getCalculatorValues (event) {
    this.removeEventListeners();

    // Build newObj off state and exclude fieldErrors
    let newObj = {};
    Object.keys(HomeBuyingSavingsCalculator.getInitialState()).forEach(key => {
      if (key !== 'fieldErrors') {
        newObj[key] = this.state[key];
      }
    });

    newObj.calculatedMonthlySavingsAmount = this.calculateMonthlySavingsAmount();
    newObj.calculatedDownPayment = this.calculateDownPayment();

    document.dispatchEvent(new CustomEvent('receiveCalculatorStateHomeBuyingSavingsCalculator', { detail: newObj}));
  }

  setCalculatorValues (event) {
    this.handleChange(event.detail.value, event.detail.name);
  }

  removeEventListeners () {
    document.removeEventListener('getCalculatorValuesHomeBuyingSavingsCalculator', this.getCalculatorValues, true);
    document.removeEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.removeEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  handleChange (value, fieldName) {
    this.setState({ [fieldName]: value })
  }

  clearState () {
    this.setState(HomeBuyingSavingsCalculator.getInitialState())
  }

  onValidation(name, error) {
    if (typeof error !== 'undefined') {
      // Has errors
      if (!this.state.fieldErrors[name]) {
        this.setState({ fieldErrors: Object.assign({}, this.state.fieldErrors, { [name]: error }) })
      }
    } else {
      // No errors
      if (this.state.fieldErrors[name]) {
        const newErrors = Object.assign({}, this.state.fieldErrors)
        delete newErrors[name]
        this.setState({ fieldErrors: newErrors })
      }
    }
  }

  calculateMonthlySavingsAmount () {
    const { currentSavings, interestOnSavings, downPayment, timeToGoal } = this.state

    const rawSavingsGoal = downPayment || this.calculateDownPayment()

    if (!rawSavingsGoal || !interestOnSavings) {
      return undefined
    }

    return requiredPaymentToGoal(rawSavingsGoal, interestOnSavings / 100, timeToGoal, currentSavings || 0)
  }

  /**
   * Formula: = Price of house * Percent down payment
   */
  calculateDownPayment () {
    const { priceOfHouse, percentDownPayment, downPayment } = this.state

    if (!priceOfHouse || !percentDownPayment) {
      return downPayment
    }

    return priceOfHouse * (percentDownPayment / 100);
  }

  render () {
    const {
      priceOfHouse,
      percentDownPayment,
      downPayment,
      currentSavings,
      timeToGoal,
      interestOnSavings,
    } = this.state

    const calculatedMonthlySavingsAmount = this.calculateMonthlySavingsAmount();
    const calculatedDownPayment = this.calculateDownPayment();
    const hasErrors = Object.keys(this.state.fieldErrors).length > 0

    return (
      <Calculator title="Homebuying Savings Calculator">
        <div className="calculator-body">
          <Form>
            <CurrencyField
              name="priceOfHouse"
              label="Price of house"
              value={priceOfHouse}
              onChange={(e, value) => this.handleChange(value, 'priceOfHouse') }
              popover={popoverMessages.priceOfHouse}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <Field
              type="select"
              name="percentDownPayment"
              label="Percent down payment"
              value={percentDownPayment}
              choices={percentDownPaymentOptions}
              onChange={(e, value) => this.handleChange(value, 'percentDownPayment') }
              popover={popoverMessages.percentDownPayment}
            />

            <CurrencyField
              name="downPayment"
              label="Down payment"
              value={downPayment}
              calculatedValue={calculatedDownPayment}
              onChange={(e, value) => this.handleChange(value, 'downPayment') }
              popover={popoverMessages.downPayment}
            />

            <CurrencyField
              name="currentSavings"
              label="Current savings"
              value={currentSavings}
              onChange={(e, value) => this.handleChange(value, 'currentSavings') }
              popover={popoverMessages.currentSavings}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <Field
              type="select"
              name="timeToGoal"
              label="Time to goal"
              value={timeToGoal}
              onChange={(e, value) => this.handleChange(value, 'timeToGoal')}
              popover={popoverMessages.timeToGoal}
              choices={timeToGoalOptions}
            />

            <PercentageField
              name="interestOnSavings"
              label="Interest on savings (%)"
              value={interestOnSavings}
              onChange={(e, value) => this.handleChange(value, 'interestOnSavings') }
              popover={popoverMessages.interestOnSavings}
              validator={minMaxValidator(0.001, 30, formatPercentage)}
              onValidation={this.onValidation}
            />
          </Form>
          <CalculatorResults>
            <CalculatorResult
              label={'You need to save this amount/month'}
              value={formatCurrency(Math.ceil((!hasErrors && calculatedMonthlySavingsAmount) || 0))}
              popover={popoverMessages.monthlySavingsAmount}
            />
          </CalculatorResults>
          <CalculatorButtons onClear={this.clearState} />
          <Annotations>
            <HelpBlock><sup>*</sup> Be aware there are other costs when buying a house including closing costs and insurance.</HelpBlock>
          </Annotations>
        </div>
      </Calculator>
    )
  }
}

embedComponent(HomeBuyingSavingsCalculator, 'tgm-home-buying-savings-calc', 'tgm-home-buying-savings-calc-embed')

export default HomeBuyingSavingsCalculator



// WEBPACK FOOTER //
// ./src/calculators/HomeBuyingSavingsCalculator.js